import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Save, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Generic CRUD table for admin panel.
 * Props:
 *  - entityName: string (e.g., 'courses')
 *  - idField: string (e.g., 'course_id')
 *  - columns: [{ key, label, type?, options? }]
 *  - formFields: [{ key, label, type, placeholder?, options? }]
 */
const AdminCrudTable = ({ entityName, idField, columns, formFields }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/${entityName}`, { withCredentials: true });
      setItems(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entityName]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => {
    setEditItem(null);
    const initial = {};
    formFields.forEach(f => {
      if (f.type === 'tags') initial[f.key] = [];
      else initial[f.key] = '';
    });
    setFormData(initial);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    const data = {};
    formFields.forEach(f => { data[f.key] = item[f.key] || (f.type === 'tags' ? [] : ''); });
    setFormData(data);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/admin/${entityName}/${editItem[idField]}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/${entityName}`, formData, { withCredentials: true });
      }
      setShowModal(false);
      fetchItems();
    } catch (e) { alert(e.response?.data?.detail || 'Error saving'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${entityName.slice(0, -1)}?`)) return;
    try {
      await axios.delete(`${API}/admin/${entityName}/${id}`, { withCredentials: true });
      fetchItems();
    } catch (e) { alert(e.response?.data?.detail || 'Error deleting'); }
  };

  const renderField = (field) => {
    const val = formData[field.key];
    const common = { style: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(88,28,135,0.12)', color: '#fff', fontSize: 14, outline: 'none', fontFamily: 'Inter, sans-serif' } };

    if (field.type === 'textarea') {
      return <textarea {...common} rows={3} value={val} onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} />;
    }
    if (field.type === 'select') {
      return (
        <select {...common} value={val} onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))}>
          <option value="">Select...</option>
          {field.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (field.type === 'tags') {
      return (
        <div>
          <input {...common} value={Array.isArray(val) ? val.join(', ') : val} onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} placeholder="Comma-separated tags" />
        </div>
      );
    }
    return <input {...common} type="text" value={val} onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} />;
  };

  const truncate = (str, len = 50) => str && str.length > len ? str.substring(0, len) + '...' : str;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <span style={{ fontSize: 14, color: '#8885a0' }}>{items.length} {entityName}</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={fetchItems} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(88,28,135,0.12)', color: '#b0adc0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={openCreate} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
            <Plus size={16} /> Add {entityName.slice(0, -1)}
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(88,28,135,0.08)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: 14, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(139,92,246,0.15)' }}>
              {columns.map(c => (
                <th key={c.key} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#8885a0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</th>
              ))}
              <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#8885a0', width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} style={{ padding: 40, textAlign: 'center', color: '#8885a0' }}>Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} style={{ padding: 40, textAlign: 'center', color: '#8885a0' }}>No {entityName} found</td></tr>
            ) : items.map(item => (
              <tr key={item[idField]} style={{ borderBottom: '1px solid rgba(139,92,246,0.08)' }}>
                {columns.map(c => (
                  <td key={c.key} style={{ padding: '12px 16px', fontSize: 13, color: '#b0adc0' }}>
                    {c.type === 'image' ? (
                      item[c.key] ? <img src={item[c.key]} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} /> : '-'
                    ) : c.type === 'tags' ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(item[c.key] || []).map(t => <span key={t} style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, background: 'rgba(139,92,246,0.15)', color: '#c4b5fd' }}>{t}</span>)}
                      </div>
                    ) : c.type === 'badge' ? (
                      <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'rgba(139,92,246,0.15)', color: '#c4b5fd' }}>{item[c.key]}</span>
                    ) : truncate(String(item[c.key] || ''))}
                  </td>
                ))}
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => openEdit(item)} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(139,92,246,0.12)', color: '#c4b5fd', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(item[idField])} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(239,68,68,0.12)', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ background: '#1a0e2e', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{editItem ? 'Edit' : 'Create'} {entityName.slice(0, -1)}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#8885a0', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {formFields.map(field => (
                <div key={field.key}>
                  <label style={{ fontSize: 13, color: '#b0adc0', marginBottom: 6, display: 'block' }}>{field.label}</label>
                  {renderField(field)}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.3)', background: 'transparent', color: '#b0adc0', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCrudTable;
